import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/lib/i18n';
import { toast } from 'sonner';
import { Users, Plus, Shield, UserX } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export default function Team() {
  const { user } = useAuth();
  const { t } = useI18n();
  const tenantId = user?.tenant_id;
  const [members, setMembers] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'agent' });
  const [inviting, setInviting] = useState(false);

  const fetchMembers = () => {
    api.get(`/tenants/${tenantId}/users`).then(r => setMembers(r.data)).catch(() => {});
  };
  useEffect(fetchMembers, [tenantId]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      await api.post(`/tenants/${tenantId}/users`, form);
      toast.success(t.memberInvited(form.name));
      setShowInvite(false);
      setForm({ name: '', email: '', password: '', role: 'agent' });
      fetchMembers();
    } catch (err) { toast.error(err.response?.data?.detail || t.error); }
    finally { setInviting(false); }
  };

  const deactivate = async (m) => {
    if (!window.confirm(t.deactivateConfirm(m.name))) return;
    try {
      await api.put(`/tenants/${tenantId}/users/${m.id}/deactivate`);
      toast.success(t.memberDeactivated);
      fetchMembers();
    } catch { toast.error(t.error); }
  };

  const changeRole = async (m, role) => {
    try {
      await api.put(`/tenants/${tenantId}/users/${m.id}/role`, { role });
      toast.success(t.roleUpdated);
      fetchMembers();
    } catch { toast.error(t.error); }
  };

  const active = members.filter(m => m.is_active !== false);

  // Presence mirrors the backend rule: explicit online marker + heartbeat within 2 minutes.
  const isOnline = (m) => {
    const marked = m.is_online === true || m.presence === 'online' || m.status === 'online';
    if (!marked || !m.last_seen) return false;
    const ts = new Date(m.last_seen).getTime();
    if (Number.isNaN(ts)) return false;
    return (Date.now() - ts) <= 120000;
  };
  const formatLastSeen = (m) => {
    if (!m.last_seen) return t.lastSeenNever;
    const d = new Date(m.last_seen);
    if (Number.isNaN(d.getTime())) return t.lastSeenNever;
    return `${t.lastSeen}: ${d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`;
  };

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[900px] mx-auto animate-fade-in-up">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-2xl text-white" data-testid="team-title">{t.teamTitle}</h2>
          <p className="text-sm text-[#a1a1aa] mt-1">{t.activeMembers(active.length)}</p>
        </div>
        <button onClick={() => setShowInvite(true)} data-testid="invite-member-btn"
          className="flex items-center gap-2 h-10 px-4 bg-[#EF694C] hover:bg-[#d65d43] text-white text-sm font-semibold rounded-lg shadow-[0_0_20px_rgba(239,105,76,0.3)] transition-all active:scale-95">
          <Plus className="w-4 h-4" /> {t.inviteMember}
        </button>
      </div>

      {members.length === 0 ? (
        <div className="text-center py-16 bg-[#121214] border border-white/10 rounded-xl">
          <Users className="w-10 h-10 text-[#52525b] mx-auto mb-3" />
          <p className="text-[#a1a1aa] text-base">{t.noTeamMembers}</p>
        </div>
      ) : (
        <div className="bg-[#121214] border border-white/10 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="px-5 py-3 text-left text-xs text-[#71717a] font-medium uppercase tracking-wider">{t.memberCol}</th>
                <th className="px-5 py-3 text-left text-xs text-[#71717a] font-medium uppercase tracking-wider">{t.emailCol}</th>
                <th className="px-5 py-3 text-left text-xs text-[#71717a] font-medium uppercase tracking-wider">{t.roleCol}</th>
                <th className="px-5 py-3 text-left text-xs text-[#71717a] font-medium uppercase tracking-wider">{t.statusCol}</th>
                <th className="px-5 py-3 text-right text-xs text-[#71717a] font-medium uppercase tracking-wider">{t.actionsCol}</th>
              </tr>
            </thead>
            <tbody>
              {members.map(m => (
                <tr key={m.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors" data-testid={`member-row-${m.id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#EF694C]/10 flex items-center justify-center">
                        <span className="text-xs font-bold text-[#EF694C]">{m.name?.charAt(0)}</span>
                      </div>
                      <span className="text-sm text-white font-medium">{m.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm text-[#a1a1aa]">{m.email}</td>
                  <td className="px-5 py-3">
                    <select value={m.role} onChange={e => changeRole(m, e.target.value)} data-testid={`role-select-${m.id}`}
                      className="h-7 bg-[#09090b] border border-white/10 rounded px-2 text-xs text-white focus:outline-none focus:border-[#EF694C]">
                      <option value="tenant_admin">Admin</option>
                      <option value="agent">Agent</option>
                    </select>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full font-medium w-fit ${isOnline(m) ? 'text-[#10B981] bg-[#10B981]/10' : 'text-[#a1a1aa] bg-white/5'}`} data-testid={`presence-${m.id}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${isOnline(m) ? 'bg-[#10B981]' : 'bg-[#52525b]'}`} />
                        {isOnline(m) ? t.presenceOnline : t.presenceOffline}
                      </span>
                      <span className="text-[10px] text-[#52525b]">{formatLastSeen(m)}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right">
                    {m.is_active !== false && m.id !== user?.id && (
                      <button onClick={() => deactivate(m)} data-testid={`deactivate-${m.id}`}
                        className="p-1.5 text-[#a1a1aa] hover:text-[#F43F5E] hover:bg-[#F43F5E]/10 rounded-lg transition-colors">
                        <UserX className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Invite Dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-md" data-testid="invite-modal">
          <DialogHeader><DialogTitle className="font-heading font-bold">{t.inviteTitle}</DialogTitle></DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4 mt-2">
            <div>
              <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">{t.fullName}</label>
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                data-testid="invite-name" required
                className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C] transition-all" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">{t.email}</label>
              <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                data-testid="invite-email" required
                className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C] transition-all" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">{t.initialPassword}</label>
              <input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                data-testid="invite-password" required
                className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C] transition-all" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">{t.role}</label>
              <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
                data-testid="invite-role"
                className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C]">
                <option value="tenant_admin">{t.admin}</option>
                <option value="agent">{t.agent}</option>
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowInvite(false)} className="flex-1 h-10 border border-white/10 text-[#a1a1aa] hover:text-white hover:bg-white/5 rounded-lg text-sm font-medium transition-all">{t.cancel}</button>
              <button type="submit" disabled={inviting} data-testid="submit-invite" className="flex-1 h-10 bg-[#EF694C] hover:bg-[#d65d43] text-white font-semibold rounded-lg text-sm transition-all disabled:opacity-60">{inviting ? t.inviting : t.sendInvite}</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
